import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, Sparkles, Crown, Rocket } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { NoahMascot } from './NoahMascot';
import { noahToast } from '../lib/noahToast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Evita que el tour reaparezca al navegar dentro de la misma sesión
const shownThisSession = new Set();

const BASIC_STEPS = [
  {
    mood: 'chat',
    title: 'Hola, soy Noah',
    text: 'Contesto a tus clientes por WhatsApp, Messenger, Instagram y SMS. Nunca dejas un mensaje sin responder, ni de madrugada.',
    route: '/', target: '[data-testid="kpi-chats"]',
  },
  {
    mood: 'focused',
    title: 'Aquí vive tu dinero',
    text: 'Este panel resume tus clientes, ingresos y conversión. Púlsalo y se abre el detalle con enlaces a cada pantalla.',
    route: '/', target: '[data-testid="kpi-clientes"]', interactive: true,
    hint: 'Pulsa la tarjeta de Clientes para ver el detalle',
  },
  {
    mood: 'focused',
    title: 'Tus ventas, en burbujas',
    text: 'Arrastra cada cliente entre columnas y mira al instante en qué etapa está cada venta. Sin hojas de cálculo.',
    route: '/bubbles', target: '.pipeline-columns',
  },
  {
    mood: 'thinking',
    title: 'Noah entiende a tus clientes',
    text: 'Saco presupuesto, urgencia y las objeciones que frenan la venta de cada chat, y te digo la próxima acción.',
    route: '/deals', target: '[data-testid="stat-hot"]',
  },
  {
    mood: 'alert',
    title: 'Te aviso cuando hace falta un humano',
    text: 'Si el cliente se enfada o pide algo delicado, abro un ticket urgente y te suena el aviso.',
    route: '/tickets', target: '[data-testid="ticket-stat-urgent"]', interactive: true,
    hint: 'Pulsa "Urgentes" para filtrar los tickets que no pueden esperar',
  },
  {
    mood: 'happy',
    title: 'Todo medido, cada semana',
    text: 'Compara campañas lado a lado y recibe cada lunes el ranking por email con la objeción que más te costó.',
    route: '/reports', target: '[data-testid="reports-mode-compare"]', interactive: true,
    hint: 'Pulsa "Comparar" y enfrenta tus campañas',
  },
  {
    mood: 'happy',
    title: 'Empieza a vender con Noah',
    text: 'Activa tu suscripción y desbloquea campañas, canales y automatizaciones sin límite. Cancelas cuando quieras.',
    route: '/plans', highlight: true,
  },
];

const PLUS_EXTRA_STEPS = [
  {
    mood: 'focused',
    title: 'Automatiza a Noah',
    text: 'Reglas por campaña: avisar a un humano, abrir ticket, pausarme o mover la etapa del cliente, con horarios y presencia de agentes.',
    route: '/automations', target: '[data-testid="automations-new-rule"]', interactive: true,
    hint: 'Pulsa "Nueva regla" y crea tu primera automatización',
  },
  {
    mood: 'search',
    title: 'Compara tus campañas',
    text: 'Enfrenta hasta 6 campañas en 10 métricas y descubre la ganadora y por cuánto gana.',
    route: '/reports', target: '[data-testid="reports-mode-compare"]',
  },
  {
    mood: 'chat',
    title: 'Conecta tus canales',
    text: 'Cada campaña usa su propio WhatsApp Cloud y su propia línea de Twilio: credenciales privadas por campaña.',
    route: '/campaigns', target: '[data-testid^="campaign-whatsapp-quick-"]',
  },
  {
    mood: 'happy',
    title: 'Cobra protegido',
    text: 'Pagos con Escrow, facturas y cotizaciones: el dinero se libera cuando el cliente confirma la entrega.',
    route: '/escrow',
  },
];

const PADDING = 10;

export const AppTour = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sub, setSub] = useState(null);
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);

  const variant = sub && sub.status && sub.status !== 'trial' ? 'plus' : 'basic';
  const planId = sub?.plan_id || 'estandar';
  const storageKey = variant === 'plus' ? `deal_tour_done_plus_${planId}` : 'deal_tour_done_basic';

  const steps = variant === 'plus'
    ? [
      {
        mood: 'happy',
        title: `Bienvenido a tu plan ${sub?.plan?.name || ''}`,
        text: `Tu suscripción desbloquea: ${(sub?.plan?.features || []).slice(0, 4).join(' · ')}.`,
        route: '/', target: '[data-testid="kpi-conversion"]',
      },
      ...PLUS_EXTRA_STEPS,
      {
        mood: 'focused',
        title: 'Tu equipo',
        text: `Tu plan admite ${sub?.plan?.max_monitors ?? 0} monitores y ${sub?.plan?.max_admins ?? 1} administrador. Invítalos desde la gestión de usuarios.`,
        route: '/admin/users',
      },
    ]
    : BASIC_STEPS;

  const step = steps[Math.min(i, steps.length - 1)];

  useEffect(() => {
    axios.get(`${API}/subscriptions/current`)
      .then((r) => setSub(r.data))
      .catch(() => setSub({ status: 'trial', plan_id: 'estandar' }));
  }, []);

  useEffect(() => {
    if (sub && !localStorage.getItem(storageKey) && !shownThisSession.has(storageKey)) {
      const t = setTimeout(() => { shownThisSession.add(storageKey); setOpen(true); }, 1200);
      return () => clearTimeout(t);
    }
  }, [sub, storageKey]);

  useEffect(() => {
    const handler = () => { setI(0); setOpen(true); };
    window.addEventListener('deal:open-tour', handler);
    return () => window.removeEventListener('deal:open-tour', handler);
  }, []);

  // Navega a la pantalla del paso y resalta el elemento real
  useEffect(() => {
    if (!open || !step) return;
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
      return;
    }
    let cancelled = false;
    setRect(null);

    const locate = (tries = 0) => {
      if (cancelled) return;
      const el = step.target ? document.querySelector(step.target) : null;
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const measure = () => {
          if (cancelled) return;
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        };
        setTimeout(measure, 380);
        rafRef.current = setInterval(measure, 500);
      } else if (tries < 8) {
        setTimeout(() => locate(tries + 1), 260);
      }
    };
    const t = setTimeout(() => locate(), 260);
    return () => {
      cancelled = true;
      clearTimeout(t);
      if (rafRef.current) clearInterval(rafRef.current);
    };
  }, [open, i, step, location.pathname, navigate]);

  // Paso interactivo: aprende haciéndolo — avanza al pulsar el elemento resaltado
  useEffect(() => {
    if (!open || !step?.interactive || !step.target) return;
    const el = document.querySelector(step.target);
    if (!el) return;
    const onClick = () => setI((prev) => (prev === steps.length - 1 ? prev : prev + 1));
    // el clic real cuenta como paso interactivo superado
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [open, i, step, rect, steps.length]);

  // Guarda el progreso de cada paso visto
  useEffect(() => {
    if (!open || !step) return;
    axios.post(`${API}/tour/progress`, {
      variant,
      step_index: i,
      step_key: step.title,
      total: steps.length,
      interactive: !!step.interactive,
    }).catch(() => {});
  }, [open, i, step, variant, steps.length]);

  const close = useCallback(() => {
    shownThisSession.add(storageKey);
    localStorage.setItem(storageKey, '1');
    if (rafRef.current) clearInterval(rafRef.current);
    setOpen(false);
    setRect(null);
  }, [storageKey]);

  const finish = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/tour/complete?variant=${variant}`);
      if (data?.first_time) {
        noahToast('happy', '¡Tour completado!', `Ganaste la insignia «${data.badge?.name}». Ya conoces todos mis trucos.`, { duration: 7000 });
      }
    } catch { /* la recompensa es un extra */ }
    close();
  }, [variant, close]);

  const next = () => (i === steps.length - 1 ? finish() : setI(i + 1));
  const prev = () => setI(Math.max(0, i - 1));

  if (!sub || !open || !step) return null;

  const anchored = !!rect && rect.width > 0;
  const tooltipTop = anchored
    ? (rect.top + rect.height + 260 > window.innerHeight ? Math.max(12, rect.top - 250) : rect.top + rect.height + 18)
    : null;
  const tooltipLeft = anchored
    ? Math.min(Math.max(12, rect.left - 20), Math.max(12, window.innerWidth - 400))
    : null;

  const card = (
    <motion.div
      key={i}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.22 }}
      className="w-[min(380px,92vw)] rounded-3xl bg-brand-deep border border-brand-green/50 p-4 shadow-2xl pointer-events-auto"
      data-testid="tour-card"
    >
      <div className="flex items-center gap-2 mb-2 text-brand-butter/80">
        {variant === 'plus' ? <Crown className="w-3.5 h-3.5" /> : <Rocket className="w-3.5 h-3.5" />}
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          {variant === 'plus' ? `Tour Plus · ${sub?.plan?.name || ''}` : 'Tour de bienvenida'}
        </span>
        <Sparkles className="w-3 h-3 ml-auto" />
        <span className="text-[10px]">{i + 1}/{steps.length}</span>
        <button onClick={close} data-testid="tour-close" className="text-white/60 hover:text-white ml-1">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex items-start gap-3">
        <NoahMascot mood={step.mood} size={78} forceMood className="shrink-0" float={false} />
        <div className="min-w-0">
          {step.highlight && (
            <Badge className="mb-1.5 bg-brand-butter text-brand-deep border-none">Recomendado</Badge>
          )}
          <h2 className="text-base font-bold text-brand-butter leading-tight">{step.title}</h2>
          <p className="text-xs text-white/80 mt-1.5">{step.text}</p>
          {step.interactive && anchored && (
            <p className="text-[11px] mt-2 px-2 py-1.5 rounded-lg bg-brand-butter/15 text-brand-butter font-medium animate-pulse"
              data-testid="tour-hint">
              👆 {step.hint || 'Pulsa el elemento resaltado para continuar'}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        {i > 0 && (
          <Button variant="ghost" size="sm" onClick={prev} data-testid="tour-prev"
            className="h-8 text-white/70 hover:text-white hover:bg-white/10 gap-1 px-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Atrás
          </Button>
        )}
        <button onClick={close} data-testid="tour-skip" className="text-[11px] text-white/50 hover:text-white/80">
          Saltar
        </button>
        {step.interactive && anchored ? (
          <button onClick={next} data-testid="tour-next"
            className="ml-auto text-[11px] text-white/50 hover:text-white/80 underline">
            Prefiero solo mirar
          </button>
        ) : (
          <Button size="sm" onClick={next} data-testid="tour-next"
            className={`h-8 ml-auto gap-1 ${step.highlight ? 'bg-brand-butter text-brand-deep hover:bg-brand-butter/90' : 'bg-brand-green text-white hover:bg-brand-green/90'}`}>
            {i === steps.length - 1 ? 'Empezar' : 'Siguiente'} <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      <div className="flex items-center gap-1.5 mt-3">
        {steps.map((_, idx) => (
          <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-5 bg-brand-butter' : 'w-1.5 bg-white/25'}`} />
        ))}
      </div>
    </motion.div>
  );

  return (
    <div data-testid="app-tour" className="fixed inset-0 z-[80] pointer-events-none">
      {/* Foco sobre el botón real */}
      {anchored ? (
        <>
          <motion.div
            data-testid="tour-spotlight"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute rounded-2xl pointer-events-none"
            style={{
              top: rect.top - PADDING,
              left: rect.left - PADDING,
              width: rect.width + PADDING * 2,
              height: rect.height + PADDING * 2,
              boxShadow: '0 0 0 9999px rgba(0,26,25,0.72), 0 0 0 2px #f4eb9f, 0 0 26px 6px rgba(244,235,159,0.45)',
            }}
          />
          {step.interactive && (
            <>
              <div className="absolute left-0 right-0 top-0 pointer-events-auto" style={{ height: Math.max(0, rect.top - PADDING) }} />
              <div className="absolute left-0 right-0 bottom-0 pointer-events-auto" style={{ top: rect.top + rect.height + PADDING }} />
              <div className="absolute left-0 pointer-events-auto" style={{ top: rect.top - PADDING, height: rect.height + PADDING * 2, width: Math.max(0, rect.left - PADDING) }} />
              <div className="absolute right-0 pointer-events-auto" style={{ top: rect.top - PADDING, height: rect.height + PADDING * 2, left: rect.left + rect.width + PADDING }} />
            </>
          )}
          <div className="absolute" style={{ top: tooltipTop, left: tooltipLeft }}>
            <AnimatePresence mode="wait">{card}</AnimatePresence>
          </div>
        </>
      ) : (
        <div className="absolute inset-0 bg-black/65 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 pointer-events-auto">
          <AnimatePresence mode="wait">{card}</AnimatePresence>
        </div>
      )}
    </div>
  );
};

export const openAppTour = () => window.dispatchEvent(new Event('deal:open-tour'));

export default AppTour;
